import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchStore, type DataSearchType } from '../../../store/jpeTopPageStore'
import { useModalScrollLock, dayOptions, monthOptions, yearOptions } from '../utils'

const IconMinus = () => (
  <svg className="h-8" fill="none" height="36" viewBox="0 0 36 36" width="36" xmlns="http://www.w3.org/2000/svg">
    <path
      clipRule="evenodd"
      d="M13 18C13 17.7348 13.1054 17.4804 13.2929 17.2929C13.4804 17.1054 13.7348 17 14 17H22C22.2652 17 22.5196 17.1054 22.7071 17.2929C22.8946 17.4804 23 17.7348 23 18C23 18.2652 22.8946 18.5196 22.7071 18.7071C22.5196 18.8946 22.2652 19 22 19H14C13.7348 19 13.4804 18.8946 13.2929 18.7071C13.1054 18.5196 13 18.2652 13 18Z"
      fill="#4B5563"
      fillRule="evenodd"
    ></path>
    <rect height="35" rx="17.5" stroke="#E5E7EB" width="35" x="0.5" y="0.5" />
  </svg>
)

const IconPlus = () => (
  <svg className="h-8" fill="none" height="40" viewBox="0 0 40 40" width="40" xmlns="http://www.w3.org/2000/svg">
    <path
      clip-rule="evenodd"
      d="M20 15C20.2652 15 20.5196 15.1054 20.7071 15.2929C20.8946 15.4804 21 15.7348 21 16V19H24C24.2652 19 24.5196 19.1054 24.7071 19.2929C24.8946 19.4804 25 19.7348 25 20C25 20.2652 24.8946 20.5196 24.7071 20.7071C24.5196 20.8946 24.2652 21 24 21H21V24C21 24.2652 20.8946 24.5196 20.7071 24.7071C20.5196 24.8946 20.2652 25 20 25C19.7348 25 19.4804 24.8946 19.2929 24.7071C19.1054 24.5196 19 24.2652 19 24V21H16C15.7348 21 15.4804 20.8946 15.2929 20.7071C15.1054 20.5196 15 20.2652 15 20C15 19.7348 15.1054 19.4804 15.2929 19.2929C15.4804 19.1054 15.7348 19 16 19H19V16C19 15.7348 19.1054 15.4804 19.2929 15.2929C19.4804 15.1054 19.7348 15 20 15Z"
      fill="#4B5563"
      fillRule="evenodd"
    ></path>
    <rect height="39" rx="19.5" stroke="#E5E7EB" width="39" x="0.5" y="0.5"></rect>
  </svg>
)

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
)

const currentYear = new Date().getFullYear()

export const JpeSearchModal: React.FC = () => {
  const { searchData, isSearchModalOpen, setSearchData, setModalOpen, applySearchAndUpdateUrl, isDesktop } = useSearchStore()
  const [localSearchData, setLocalSearchData] = useState<DataSearchType>(searchData)
  const [validationErrors, setValidationErrors] = useState<{
    childBirthdate?: boolean
    minChildren?: boolean
  }>({})
  const { t } = useTranslation()
  const DAY_OPTIONS = useMemo(() => dayOptions(), [])
  const MONTH_OPTIONS = useMemo(() => monthOptions(t), [t])
  const YEAR_OPTIONS = useMemo(() => yearOptions(), [])

  // Sync local state with store when modal opens
  useEffect(() => {
    if (isSearchModalOpen) {
      setLocalSearchData(searchData)
    }
  }, [isSearchModalOpen, searchData])

  useModalScrollLock(isSearchModalOpen)

  // Counter handlers
  const handleChangeCounter = (field: keyof DataSearchType, direction: 'up' | 'down') => {
    const currentValue = localSearchData[field] as number
    // 大人・子どものカウンターは最小値1、その他は0
    const minValue = field === 'number_of_children' || field === 'number_of_adults' ? 1 : 0
    let newValue = direction === 'up' ? currentValue + 1 : Math.max(minValue, currentValue - 1)

    // Apply maximum limit for children
    if (field === 'number_of_children' && newValue > 20) {
      newValue = 20
    }

    const updatedData = { ...localSearchData, [field]: newValue }

    // Special handling for children count
    if (field === 'number_of_children') {
      const birthdays = [...localSearchData.birthdays]
      if (direction === 'up' && newValue <= 20) {
        // Add empty birthday for new child
        birthdays.push({ day: '', year: '', month: '' })
      } else if (direction === 'down' && birthdays.length > 0) {
        // Remove last birthday
        birthdays.pop()
      }
      updatedData.birthdays = birthdays
    }

    // Clear validation errors when user makes changes
    if (validationErrors.childBirthdate) {
      setValidationErrors({})
    }

    setLocalSearchData(updatedData)
  }

  // Birthday change handler
  const handleChangeBirthday = (childIndex: number, field: 'year' | 'month' | 'day', value: string) => {
    const birthdays = [...localSearchData.birthdays]
    birthdays[childIndex] = {
      ...birthdays[childIndex],
      [field]: value,
    }

    // Clear validation errors when user makes changes
    if (validationErrors.childBirthdate) {
      setValidationErrors({})
    }

    setLocalSearchData({ ...localSearchData, birthdays: birthdays })
  }

  // Stay duration handler (single select)
  const handleChangeStayDuration = (weeks: string) => {
    const days = String(Number(weeks) * 7)
    setLocalSearchData({ ...localSearchData, stay_day: days })
  }

  // Month selection handler (max 3)
  const handleToggleMonth = (monthValue: string) => {
    const currentMonths = localSearchData.plan_month
    const isSelected = currentMonths.includes(monthValue)

    let updatedMonths: string[]
    if (isSelected) {
      updatedMonths = currentMonths.filter((m) => m !== monthValue)
    } else if (currentMonths.length >= 3) {
      return
    } else {
      updatedMonths = [...currentMonths, monthValue]
    }

    setLocalSearchData({ ...localSearchData, plan_month: updatedMonths })
  }

  const handleSubmit = () => {
    // Validate minimum children count
    if (localSearchData.number_of_children < 1) {
      setValidationErrors({ minChildren: true })
      return
    }

    // Validate child birthdays: if children are added, both month AND year must be selected
    if (localSearchData.number_of_children > 0) {
      const hasIncompleteBirthdays = localSearchData.birthdays.some(
        (birthday) => !birthday.day || !birthday.month || !birthday.year,
      )

      if (hasIncompleteBirthdays) {
        setValidationErrors({ childBirthdate: true })
        return
      }
    }

    // Clear validation errors if form is valid
    setValidationErrors({})
    setSearchData(localSearchData)
    setModalOpen(false)
    applySearchAndUpdateUrl()
  }

  const handleClose = () => {
    setModalOpen(false)
  }

  const renderPlanMonths = (year: number) => {
    const isCurrentYear = year === currentYear
    const currentMonth = new Date().getMonth() + 1

    return (
      <div key={year} className="mt-4">
        <div className="text-sm font-medium text-gray-700 mb-2">{year}</div>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
            const monthValue = `${year}/${month.toString().padStart(2, '0')}`
            const isSelected = localSearchData.plan_month.includes(monthValue)
            const isPast = isCurrentYear && month < currentMonth

            // Hide past months beyond certain thresholds
            if (isCurrentYear && currentMonth > 4 && month <= 4) return null
            if (isCurrentYear && currentMonth > 8 && month <= 8) return null

            return (
              <button
                key={month}
                disabled={isPast}
                onClick={() => handleToggleMonth(monthValue)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-white'
                    : isPast
                      ? 'bg-gray-100 text-gray-400 line-through cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : isPast ? {} : {}}
              >
                {t(`month.abbr_month_names.${month}`) || MONTH_OPTIONS[month - 1].label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  if (!isSearchModalOpen) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isDesktop ? 'items-center' : 'items-start'}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        className={`bg-white shadow-2xl flex flex-col font-noto ${isDesktop ? 'rounded-2xl' : ''}`}
        style={{
          animation: isDesktop ? 'slide-down 0.4s ease-in-out forwards' : 'slide-up 0.3s ease-in-out forwards',
          width: isDesktop ? '480px' : '100%',
          height: isDesktop ? '90vh' : '100%',
          borderRadius: isDesktop ? '16px' : '0',
          maxWidth: isDesktop ? '90vw' : 'none',
        }}
      >
        <div className="flex justify-between p-4">
          <h2 className="text-lg font-normal text-gray-900 pr-8">Enter your child's details to see matching preschools</h2>
          <button onClick={handleClose} className="shrink-0 p-1 rounded-full hover:bg-gray-100 transition-colors">
            <CloseIcon />
          </button>
        </div>

        {validationErrors.minChildren && (
          <div className="mx-4 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">Please enter at least 1 child.</p>
          </div>
        )}
        {validationErrors.childBirthdate && (
          <div className="mx-4 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">Please select day, month and year for all children's date of birth.</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="space-y-6">
            <h3 className="font-normal text-gray-900">Number of Guests</h3>

            <div className="flex items-center justify-between">
              <span className="text-gray-700">Adult</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChangeCounter('number_of_adults', 'down')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600 disabled:opacity-30"
                  disabled={localSearchData.number_of_adults <= 1}
                >
                  <IconMinus />
                </button>
                <span className="w-8 text-center font-medium">{localSearchData.number_of_adults}</span>
                <button
                  onClick={() => handleChangeCounter('number_of_adults', 'up')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600"
                >
                  <IconPlus />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-700">Child</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChangeCounter('number_of_children', 'down')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600 disabled:opacity-30"
                  disabled={localSearchData.number_of_children <= 1}
                >
                  <IconMinus />
                </button>
                <span className="w-8 text-center font-medium">{localSearchData.number_of_children}</span>
                <button
                  onClick={() => handleChangeCounter('number_of_children', 'up')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600 disabled:opacity-30"
                  disabled={localSearchData.number_of_children >= 20}
                >
                  <IconPlus />
                </button>
              </div>
            </div>
          </div>

          {localSearchData.number_of_children > 0 && (
            <div className="space-y-4">
              {localSearchData.birthdays.map((birthday, index) => {
                const hasError = validationErrors.childBirthdate && (!birthday.day || !birthday.month || !birthday.year)
                return (
                  <div key={index} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Child {index + 1} - Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={birthday.day}
                        onChange={(e) => handleChangeBirthday(index, 'day', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        style={hasError && !birthday.day ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                      >
                        <option value="">Day</option>
                        {DAY_OPTIONS.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={birthday.month}
                        onChange={(e) => handleChangeBirthday(index, 'month', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        style={hasError && !birthday.month ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                      >
                        <option value="">Month</option>
                        {MONTH_OPTIONS.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={birthday.year}
                        onChange={(e) => handleChangeBirthday(index, 'year', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        style={hasError && !birthday.year ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                      >
                        <option value="">Year</option>
                        {YEAR_OPTIONS.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Not in the Program counters */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">
                Not in the Program
                <br />
                (Aged 0-6)
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChangeCounter('number_of_preschoolers_1', 'down')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600 disabled:opacity-30"
                  disabled={localSearchData.number_of_preschoolers_1 === 0}
                >
                  <IconMinus />
                </button>
                <span className="w-8 text-center font-medium">{localSearchData.number_of_preschoolers_1}</span>
                <button
                  onClick={() => handleChangeCounter('number_of_preschoolers_1', 'up')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600"
                >
                  <IconPlus />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">
                Not in the Program
                <br />
                (Aged 7-12)
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChangeCounter('number_of_students_1', 'down')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600 disabled:opacity-30"
                  disabled={localSearchData.number_of_students_1 === 0}
                >
                  <IconMinus />
                </button>
                <span className="w-8 text-center font-medium">{localSearchData.number_of_students_1}</span>
                <button
                  onClick={() => handleChangeCounter('number_of_students_1', 'up')}
                  className="p-1 rounded-full hover:border-gray-400 text-gray-600"
                >
                  <IconPlus />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-regular text-gray-900">Select your stay duration</h3>
            <div className="flex gap-2">
              {['1', '2', '3', '4'].map((weeks) => {
                const days = String(Number(weeks) * 7)
                const isSelected = localSearchData.stay_day === days
                return (
                  <button
                    key={weeks}
                    onClick={() => handleChangeStayDuration(weeks)}
                    className={`flex-1 px-2 py-2 rounded-full text-sm font-medium transition-colors ${
                      isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                    }`}
                    style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
                  >
                    {weeks} week
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-normal text-gray-900">Select your preferred months (up to 3)</h3>
            {renderPlanMonths(currentYear)}
            {renderPlanMonths(currentYear + 1)}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={handleSubmit}
            className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}
